import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center text-base cursor-pointer text-base gap-2 whitespace-nowrap rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "",
        primary: "text-white",
        secondary: "",
        link: "text-primary underline-offset-4 hover:underline",
        outline: "",
        ghost: "bg-transparent",
        plain: "p-0 bg-transparent",
      },
      variantColor: {
        default: "",
        primary: "",
        secondary: "",
        destructive: "",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8 [&_svg]:size-4",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        variantColor: "primary",
        class: [
          "bg-[#489BA8]",
          "text-white",
          "hover:bg-[#489BA8]/90",
          "border-transparent",
        ],
      },
      {
        variant: "primary",
        variantColor: "secondary",
        class: [
          "bg-[#9E7926]",
          "text-white",
          "hover:bg-[#9E7926]/90",
          "border-transparent",
        ],
      },
      {
        variant: "primary",
        variantColor: "destructive",
        class: ["bg-[#FF0909]", "text-white", "hover:bg-destructive/90"],
      },
      {
        variant: "secondary",
        variantColor: "secondary",
        class: ["bg-[#F6F2EA]", "text-[#9E7926]", "hover:bg-[#F6F2EA]/90"],
      },
      {
        variant: "outline",
        variantColor: "secondary",
        class: [
          "bg-transparent",
          "text-[#9E7926]",
          "hover:bg-[#9E7926]/10",
          "border",
          "border-[#9E7926]",
        ],
      },
      {
        variant: "outline",
        variantColor: "destructive",
        class: [
          "bg-transparent",
          "text-[#FF0303]",
          "hover:bg-[#FF0303]/10",
          "border",
          "border-[#FF0303]",
        ],
      },
      {
        variant: "default",
        variantColor: "primary",
        class: ["bg-white", "text-[#191818]", "hover:bg-[#191818]/10"],
      },
      {
        variant: "default",
        variantColor: "default",
        class: [
          "border border-[#E5E7EB] bg-transparent text-[#4B5563] hover:bg-[#4B5563]/10",
        ],
      },
      {
        variant: "ghost",
        variantColor: "primary",
        class: ["text-[#489BA8]", "hover:bg-[#489BA8]/10"],
      },
      {
        variant: "ghost",
        variantColor: "secondary",
        class: ["text-[#9E7926]", "hover:bg-[#9E7926]/10"],
      },
      {
        variant: "ghost",
        variantColor: "destructive",
        class: ["text-[#FF0909]", "hover:bg-[#FF0909]/10"],
      },
      {
        variant: "ghost",
        variantColor: "default",
        class: ["text-[#191818]", "hover:bg-[#191818]/10"],
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, variantColor, asChild = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ size, variant, variantColor, className })
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
